import React, { FormEvent, useMemo, useRef, useState } from 'react';
import { ColDef, ColGroupDef, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, ValueGetterParams } from 'ag-grid-community';
import { Alert, Col, Row } from 'react-bootstrap';
import { GridWrapper } from '../strong-box/styles/styles'
import { AgGridReact } from 'ag-grid-react';
import { LoadingIndicator } from '@nkr/components/loading-indicator';
import { TLegendEntry } from '../strong-box/legend';
import { Utility } from '@nkr/services/utility';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { IStrongBoxOpoFilter, IStrongBoxOpoListing, IStrongBoxOpoOrderContract } from '@nkr/models/strong-box';
import useStrongBoxOpos from '@nkr/store/strong-box-opo/hooks/use-strong-box-opos';
import SortableHeaderComponent from '../recipients/recipient-shield/components/sortable-header';
import StrongBoxFilter from '../strong-box/strong-box-filter';
import PlaceOrder, { EntityType } from '../strong-box/place-order';
import AssignCenters from './assign-centers';
import OpoName from './opo-name';

type ColumnDef = ColDef | ColGroupDef;

const StrongBoxOpos = () => {
    const { app: { user } } = useAppHookContext();
    const [filter, setFilter] = useState<IStrongBoxOpoFilter>({ isCenter: false, opoId: user.opoId == 0 ? null : user.opoId });
    const [assignCenters, setAssignCenters] = useState<{ show: boolean, initialOpoId: string }>({ show: false, initialOpoId: null });
    const { list, loading, error, refreshData, placeOrder } = useStrongBoxOpos(filter)

    if (error) {
        return <Alert bsStyle='danger'>error</Alert>
    }

    const handlePlaceOrder = (entityId: number, quantity: number, notes: string) => {
        let order: IStrongBoxOpoOrderContract = {
            opoId: entityId,
            quantity,
            notes
        };

        placeOrder(order);
    };

    const colDefs: ColumnDef[] = [
        {
            colId: 'rNum',
            field: 'rNum',
            headerName: 'Center',
            sortable: false,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
            width: 20,
        },
        {
            field: 'opoName',
            headerName: 'OPO',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>OPO</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxOpoListing = params.data;
                return (
                    <OpoName
                        opoId={d.opoId}
                        opoName={d.opoName}
                        onOpenAssign={opoId => setAssignCenters({ show: true, initialOpoId: opoId.toString() })}
                    />
                )
            }
        },
        {
            colId: 'opoId',
            field: 'opoId',
            hide: true,
        },
        {
            groupId: 'physicalInventory',
            headerName: 'Physical Inventory',
            headerClass: 'my-group-header',
            children: [
                {
                    colId: 'lastPhysicalInventoryDate',
                    field: 'lastPhysicalInventoryDate',
                    headerName: 'Last Report Date of Physical Inventory',
                    width: 100,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Report Date</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        const d: IStrongBoxOpoListing = params.data;

                        return (
                            <>{d.lastPhysicalInventoryDate ? d.lastPhysicalInventoryDate : '--'}</>
                        );
                    },
                    comparator: Utility.dateComparator
                },
                {
                    colId: 'lastPhysicalInventoryCount',
                    field: 'lastPhysicalInventoryCount',
                    headerName: 'Physical Inventory Reported',
                    width: 100,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Reported Count</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        const d: IStrongBoxOpoListing = params.data;

                        return (
                            <>{!d.lastPhysicalInventoryCount && d.lastPhysicalInventoryCount !== 0 ? '--' : d.lastPhysicalInventoryCount}</>
                        );
                    },
                },
            ]
        },
        {
            groupId: 'sinceLastPhysicalInventory',
            headerName: 'Since Last Report',
            headerClass: 'my-group-header',
            children: [
                {
                    colId: 'unitsOrderedSinceLastPhysicalInventory',
                    field: 'unitsOrderedSinceLastPhysicalInventory',
                    headerName: 'Units Ordered Since Last Report',
                    width: 120,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Units Ordered</SortableHeaderComponent>,
                },
                {
                    colId: 'unitsDeliveredSinceLastPhysicalInventory',
                    field: 'unitsDeliveredSinceLastPhysicalInventory',
                    headerName: 'Units Delivered Since Last Report',
                    width: 120,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Units Delivered</SortableHeaderComponent>,
                },
                {
                    colId: 'unitsUtilizedSinceLastPhysicalInventory',
                    field: 'unitsUtilizedSinceLastPhysicalInventory',
                    headerName: 'Units Utilized Since Last Report',
                    width: 120,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Units Utilized</SortableHeaderComponent>,
                },
            ]
        },
        {
            groupId: 'inventory',
            headerName: 'Inventory',
            headerClass: 'my-group-header',
            children: [
                {
                    colId: 'quantityOnHand',
                    field: 'quantityOnHand',
                    headerName: 'On Hand Inventory',
                    width: 90,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>On Hand</SortableHeaderComponent>,
                },
                {
                    colId: 'minimumInventoryLevel',
                    field: 'minimumInventoryLevel',
                    headerName: 'Minimum Inventory Level',
                    width: 100,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Minimum Level</SortableHeaderComponent>,
                },
            ]
        },
        {
            colId: 'action',
            field: '',
            headerName: '',
            sortable: false,
            width: 92,
            // hide: true,
            hide: user.opoId > 0,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxOpoListing = params.data;

                return (
                    <PlaceOrder
                        loading={loading}
                        entityType={EntityType.OPO}
                        entityId={d.opoId}
                        entityName={d.opoName}
                        onPlaceOrder={handlePlaceOrder}
                    />
                );
            },
        },
    ];

    const columnDefs = useMemo(() => colDefs, []);

    const apiRef = useRef<GridApi | null>(null);

    const onGridReady = (params: GridReadyEvent) => {
        apiRef.current = params.api;
        params.api.sizeColumnsToFit();
    };

    const handleExpCsv = () => {
        if (apiRef.current) {
            apiRef.current.exportDataAsCsv({
                columnGroups: false,
                fileName: `StrongBoxOpos.csv`,
                columnKeys: [
                    'opoName',
                    'lastPhysicalInventoryDate',
                    'lastPhysicalInventoryCount',
                    'unitsOrderedSinceLastPhysicalInventory',
                    'unitsDeliveredSinceLastPhysicalInventory',
                    'unitsUtilizedSinceLastPhysicalInventory',
                    'minimumInventoryLevel'
                ],
            });
        }
    };

    const handleAssignCentersSaved = () => {
        setAssignCenters({ show: false, initialOpoId: null });
        refreshData();
    };

    const legendEntries: TLegendEntry[] = [
        { text: 'On hand inventory is below minimum level', color: 'yellow' }
    ];

    const filteredList = user.opoId ? list.filter(x => x.opoId == user.opoId) : list;

    return (
        <>
            {!user.opoId && (
                <Row>
                    <Col sm={12}>
                        <StrongBoxFilter
                            filter={filter}
                            onExport={handleExpCsv}
                            defaultFilter={{ isCenter: false }}
                            legendEntries={legendEntries}
                            isCenter={false}
                            onChange={setFilter}
                            onAssignCenters={() => setAssignCenters({ show: true, initialOpoId: null })}
                        />
                    </Col>
                </Row>
            )}

            {loading && <LoadingIndicator type='spin' color='black' loading={true} message={'Loading Inventory ...'} />}

            {!loading &&
                <Row>
                    <Col sm={12}>
                        <GridWrapper className='ag-theme-bootstrap'>
                            <AgGridReact
                                defaultColDef={{
                                    headerClass: 'list-header',
                                    cellClass: 'list-cell',
                                    sortable: true
                                }}
                                columnDefs={columnDefs}
                                rowData={filteredList}
                                headerHeight={30}
                                groupHeaderHeight={30}
                                rowHeight={32}
                                noRowsOverlayComponentFramework={() => <div>No StrongBox OPO records were found...</div>}
                                suppressRowClickSelection={true}
                                suppressCellSelection={true}
                                enableCellTextSelection={true}
                                groupDefaultExpanded={1}
                                groupSelectsChildren={true}
                                onGridReady={onGridReady}
                                onFirstDataRendered={(e) => e.api.sizeColumnsToFit()}
                                onGridSizeChanged={(e) => e.api.sizeColumnsToFit()}
                                onRowDataChanged={(e) => e.api.sizeColumnsToFit()}
                            />
                        </GridWrapper>
                    </Col>
                </Row>
            }

            <AssignCenters
                show={assignCenters.show}
                initialOpoId={assignCenters.initialOpoId}
                onSave={handleAssignCentersSaved}
                onCancel={() => setAssignCenters({ show: false, initialOpoId: null })}
            />
        </>
    );
}

export default StrongBoxOpos;
